<template lang="pug">
v-row.my-4.mt-sm-8(no-gutters)
  v-col(cols="12", :style="wysiwygContainerStyles")
    BasicWysiwyg(
      :showControls="false",
      v-model="formValue",
      placeholder="Hier klicken und Dein Kommentar eingeben (Maximum 300 Zeichen).",
      :inline="true"
      @input="validateComment",
      :getMentionables="getMentionableFunc"
    )
  v-col(cols="8", :style="buttonContainerStyles")
    v-btn.suus__comment-form__button(
      text,
      block,
      color="mediumGrey",
      :disabled="disabled",
      @click="handleSubmit"
    )
      span(:style="buttonLabelStyles") Speichern
  v-col(cols="4", :style="buttonContainerStyles")
    v-btn.suus__comment-form__button(
      text,
      block,
      color="mediumGrey",
      @click="cancelAndReset"
    )
      span(:style="cancelButtonLabelStyles") Abbrechen
  v-col(cols="12")
    p.error--text.mt-1(:style="errorMessageStyles", v-if="errorText") {{ errorText }}
</template>

<script lang="ts">
import { ref, Ref, computed, defineComponent, PropType, watch } from "vue"

import THEME from "../../constants/theme"
import { HTMlfromString, getTextContextLength } from "helpers/parseHTML"
import useMentionSearch from "concerns/useMentionSearch"
import { ViewContext } from "concerns/useContextName"
import BasicWysiwyg from "components/Wysiwyg/BasicWysiwyg.vue"
import { useConfirmationService } from "features/common/confirmation-dialog/confirmation.service"
import { useThemeColors } from "features/common/theme/useTheme"

export default defineComponent({
  name: "CommentForm",
  components: { BasicWysiwyg },
  props: {
    viewContext: { type: Object as PropType<ViewContext> },
    value: {
      type: String,
      default: "",
    },
    inline: { type: Boolean, default: false },
  },
  setup(props, context) {
    const themeColors = useThemeColors()
    const formValue = ref(props.value)
    watch(props, (props) => (formValue.value = props.value))
    let errorText: Ref<string> = ref("")
    let disabled: Ref<boolean> = computed(() => !!errorText.value.length)
    const commentRules: ((v: any) => string)[] = [
      (v) => {
        const isEmpty = (!v && !v.length) || v.toString() === "<p></p>"
        return isEmpty ? "Bitte gib ein Kommentar ein." : ""
      },
      (v) => {
        const textLength = getTextContextLength(HTMlfromString(v))
        return textLength > 300
          ? "Dein Kommentar überschreitet das Maximum von 300 Zeichen."
          : ""
      },
    ]

    function validateComment(comment: string): boolean {
      errorText.value = commentRules.reduce(
        (errors, rule) => errors + rule(comment),
        ""
      )
      return !errorText.value
    }
    function handleSubmit(value: string): void {
      if (!validateComment(formValue.value)) return
      context.emit("save", formValue.value)
      formValue.value = ""
    }

    const buttonLabelStyles = computed(() => ({
      color: disabled.value ? themeColors.mediumGrey : themeColors.primary,
      ...THEME.typography.button,
    }))
    const cancelButtonLabelStyles = computed(() => ({
      color: themeColors.darkGrey,
      ...THEME.typography.button,
    }))
    const buttonContainerStyles = {
      backgroundColor: "#fff",
      borderBottomLeftRadius: "4px",
      borderBottomRightRadius: "4px",
      border: `1px solid ${themeColors.mediumGrey}`,
    }
    const wysiwygContainerStyles = {
      borderTopLeftRadius: "4px",
      borderTopRightRadius: "4px",
      border: `1px solid ${themeColors.mediumGrey}`,
      borderBottom: "none",
    }
    const errorMessageStyles = {
      ...THEME.typography.inputMessage,
    }

    const { mentionableFunction: getMentionableFunc } = useMentionSearch(
      props.viewContext
    )

    const confirmationService = useConfirmationService()
    const _commitReset = () => {
      context.emit("cancel")
      formValue.value = props.value
      errorText.value = ""
    }
    const cancelAndReset = () => {
      if (!formValue.value) {
        _commitReset()
        return
      }
      confirmationService.open({
        title: "Änderungen verwerfen?",
        text: "Möchtest du wirklich deine Änderungen verwerfen?",
        confirmText: "Verwerfen",
        cancelText: "Abbrechen",
        onConfirm: _commitReset,
      })
    }

    return {
      formValue,
      getMentionableFunc,
      buttonLabelStyles,
      buttonContainerStyles,
      wysiwygContainerStyles,
      cancelButtonLabelStyles,
      errorMessageStyles,
      validateComment,
      handleSubmit,
      cancelAndReset,
      errorText,
      disabled,
    }
  },
})
</script>

<style lang="sass" scoped>
.suus__comment-form__button
  border-top-left-radius: 0px
  border-top-right-radius: 0px
</style>
